import { useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError, InteractionStatus } from "@azure/msal-browser";
import { apiRequest } from "../config/authConfig";

// Module-level flag to ensure only one sign-out is triggered even when
// multiple concurrent API calls fail at the same time.
let signingOut = false;

export const useApiClient = () => {
  const { instance, accounts, inProgress } = useMsal();

  const getAccessToken = useCallback(async (forceRefresh = false): Promise<string | null> => {
    if (accounts.length === 0) return null;

    try {
      const response = await instance.acquireTokenSilent({
        ...apiRequest,
        account: accounts[0],
        forceRefresh,
      });
      // Successful — reset the sign-out guard so future expiries work
      signingOut = false;
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // acquireTokenSilent already tried a hidden iframe to recover silently.
        // If that failed too, the Azure AD session is fully expired.
        // Do NOT do acquireTokenRedirect — that causes the reload loop.
        // Just sign the user out cleanly, once.
        if (inProgress === InteractionStatus.None && !signingOut) {
          signingOut = true;
          console.warn('Session expired — signing out.');
          instance.logoutRedirect().catch(console.error);
        }
        return null;
      }
      console.error("Failed to acquire token:", error);
      return null;
    }
  }, [instance, accounts, inProgress]);

  const apiCall = useCallback(async (
    url: string,
    options: RequestInit = {}
  ): Promise<Response> => {
    const token = await getAccessToken();

    const buildHeaders = (accessToken: string | null) => ({
      ...options.headers,
      ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      "Content-Type": "application/json",
    });

    let response = await fetch(url, { ...options, headers: buildHeaders(token) });
    if (response.status === 401 && token) {
      const refreshedToken = await getAccessToken(true);
      if (refreshedToken) {
        response = await fetch(url, { ...options, headers: buildHeaders(refreshedToken) });
      }
    }

    return response;
  }, [getAccessToken]);

  return { apiCall, getAccessToken };
};